import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Flex, Text } from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { AppBar } from "../../../components/mobileUi";
import {
  ALL_VALUE,
  CenterSpinner,
  EmptyText,
  FilterField,
  FilterPanel,
  ListHeaderRow,
  LoadMore,
  M,
  Png,
  SelectSheet,
  StatCard,
  SummaryCard,
  buildMonthOptions,
  useSheet,
} from "../../../components/managementUi";
import {
  DesktopPage,
  DesktopPanel,
  DesktopStatRow,
  DesktopTable,
  useIsDesktop,
} from "../../../components/desktopUi";
import { appImages } from "../../../assets/appicons";
import { useEmployeeAuth } from "../../../auth/EmployeeAuthContext";
import { JOB_TITLE, isSupportDepartment } from "../../../utils/managerRole";
import { ALL_OPTION, buildOptionSheetData, useFilterOptions, validId } from "../../../utils/useFilterOptions";
import { getBlockKpiListApi } from "../../../api/statisticalReport.api";
import { EMP_BASE_PATH } from "../../../config";

const LIMIT = 20;
const DIRECTOR_JOB_CODES = [JOB_TITLE.TGD, JOB_TITLE.PTGD, JOB_TITLE.GDCN];

// Khớp BLOCK_KPI_STATUS_DESCRIPTIONS của app.
export const BLOCK_KPI_STATUS_LABELS = {
  XUAT_SAC: "Xuất sắc",
  TOT: "Tốt",
  CAN_CO_GANG: "Cần cố gắng",
  CANH_BAO: "Cảnh báo",
};

const STATUS_OPTIONS = [
  { key: ALL_VALUE, label: "Tất cả" },
  ...Object.keys(BLOCK_KPI_STATUS_LABELS).map((k) => ({ key: k, label: BLOCK_KPI_STATUS_LABELS[k] })),
];
const SORT_OPTIONS = [
  { key: "NEWEST", label: "Mới đến cũ" },
  { key: "OLDEST", label: "Cũ đến mới" },
];

export const blockKpiStatusUi = (code) => {
  if (code === "XUAT_SAC") return { bg: "#DDEDFF", color: "#1277EB", icon: appImages.icon_award };
  if (code === "TOT") return { bg: "#EDFDF5", color: "#2ABA82", icon: appImages.icon_trending_up };
  if (code === "CAN_CO_GANG") return { bg: "#F5F3FF", color: "#8B5CF6", icon: appImages.icon_heart_handshake };
  return { bg: "#FFF7E8", color: "#F79D0D", icon: appImages.icon_triangle_alert };
};

const fmtScore = (v) => (Number.isInteger(Number(v)) ? `${Number(v)}` : `${Math.round(Number(v) * 100) / 100}`);

const ScoreBox = ({ icon, score, weight, label, active, activeColor, activeBg }) => (
  <Flex
    direction="column"
    align="center"
    justify="center"
    gap="8px"
    p="12px"
    borderRadius="20px"
    border="1px solid"
    borderColor={active ? activeColor : "#F7F9FC"}
    bg={active ? activeBg : "#F7F9FC"}
    w="23%"
  >
    <Png src={icon} size={16} color={active ? activeColor : M.grey200} />
    <Flex align="flex-end">
      <Text fontSize="16px" fontWeight={600} color={M.baseContent}>
        {fmtScore(score)}
      </Text>
      <Text fontSize="12px" fontWeight={500} color={M.grey200}>
        /{weight}
      </Text>
    </Flex>
    <Text fontSize="10px" fontWeight={600} color={active ? activeColor : M.grey200} whiteSpace="nowrap">
      {label}
    </Text>
  </Flex>
);

const BlockKpiCard = ({ item, onClick }) => {
  const cls = item?.classification || {};
  const ui = blockKpiStatusUi(cls?.code);
  const m = item?.metrics || {};
  const boxes = [
    {
      key: "recruitment",
      icon: appImages.icon_user_plus,
      label: "TUYỂN DỤNG",
      score: m.recruitmentScore || 0,
      weight: 20,
      active: !!m.hasMetRecruitmentCriterion,
      activeColor: "#8B5CF6",
      activeBg: "#F5F3FF",
    },
    {
      key: "attendance",
      icon: appImages.icon_user_check,
      label: "CHUYÊN CẦN",
      score: m.attendanceScore || 0,
      weight: 20,
      active: !!m.hasMetAttendanceCriterion,
      activeColor: "#2F80ED",
      activeBg: "#EFF6FF",
    },
    {
      key: "training",
      icon: appImages.icon_award,
      label: "ĐÀO TẠO",
      score: m.trainingScore || 0,
      weight: 20,
      active: !!m.hasMetTrainingCriterion,
      activeColor: "#2ABA82",
      activeBg: "#EDFDF5",
    },
    {
      key: "transaction",
      icon: appImages.icon_circle_dollar_sign,
      label: "GIAO DỊCH",
      score: m.transactionScore || 0,
      weight: 40,
      active: !!m.hasMetTransactionCriterion,
      activeColor: "#F57127",
      activeBg: "#FFF7ED",
    },
  ];

  return (
    <Box
      as="button"
      type="button"
      w="100%"
      textAlign="left"
      onClick={onClick}
      bg="white"
      border="1px solid"
      borderColor={M.border}
      borderRadius="12px"
      p="14px"
    >
      <Flex align="center" justify="space-between" gap="8px">
        <Text fontSize="14px" fontWeight={600} color={M.baseContent} noOfLines={2} flex="1">
          {item?.block?.name || "-"}
        </Text>
        <Flex align="center" gap="6px" bg={ui.bg} borderRadius="6px" px="8px" py="3px" flexShrink={0}>
          <Png src={ui.icon} size={14} color={ui.color} />
          <Text fontSize="12px" fontWeight={600} color={ui.color}>
            {cls?.label || BLOCK_KPI_STATUS_LABELS[cls?.code] || "-"}
          </Text>
        </Flex>
      </Flex>

      <Box h="8px" />

      <Flex direction="column" gap="8px">
        <Flex align="center" gap="4px">
          <Png src={appImages.icon_user_check} size={12} color={M.gray} />
          <Text fontSize="12px" fontWeight={600} color={M.gray}>
            GĐ khối: {item?.block?.managerName || "-"}
            {item?.block?.managerCode ? ` (${item.block.managerCode})` : ""}
          </Text>
        </Flex>
        <Flex align="center" gap="4px">
          <Png src={appImages.icon_map_pin} size={12} color={M.grey200} />
          <Text fontSize="12px" fontWeight={600} fontStyle="italic" color={M.grey200}>
            Vùng: {item?.block?.areaName || "-"}
          </Text>
        </Flex>
        {item?.block?.branchName ? (
          <Flex align="center" gap="4px">
            <Png src={appImages.icon_building_2} size={12} color={M.grey200} />
            <Text fontSize="12px" color={M.grey200}>
              {item.block.branchName}
            </Text>
          </Flex>
        ) : null}
      </Flex>

      <Box h="12px" />

      <Flex align="center" justify="space-between" gap="6px">
        {boxes.map((b) => (
          <ScoreBox key={b.key} {...b} />
        ))}
      </Flex>

      <Box h="12px" />

      <Flex align="center" justify="space-between" gap="8px">
        <Flex align="center" gap="6px">
          <Text fontSize="12px" fontWeight={600} fontStyle="italic" color={M.tertiary}>
            Nhân sự tuyển mới / Nghỉ:
          </Text>
          <Text fontSize="12px" fontWeight={600} fontStyle="italic" color={M.baseContent}>
            {Number(m.newStaffCount || 0)} / {Number(m.resignedStaffCount || 0)}
          </Text>
        </Flex>
        <Flex align="center" gap="4px" flexShrink={0}>
          <Text fontSize="12px" fontWeight={600} color={M.tertiary}>
            Điểm số:
          </Text>
          <Text fontSize="14px" fontWeight={600} color={M.primary}>
            {fmtScore(m.scorePercent ?? m.score ?? 0)}
          </Text>
        </Flex>
      </Flex>
    </Box>
  );
};

const BlockKpiList = () => {
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const { user } = useEmployeeAuth();
  const monthOptions = useMemo(() => buildMonthOptions(24), []);

  const jobPosition = user?.jobPosition?.code || "";
  const supportDept = isSupportDepartment(user?.supportDepartment?.code);
  const isDirector = DIRECTOR_JOB_CODES.includes(jobPosition);
  // Trưởng vùng chỉ xem khối thuộc vùng của họ -> khoá areaId theo user (khớp app).
  const isAreaLockedToUser = !supportDept && !isDirector && jobPosition === JOB_TITLE.TRUONG_VUNG;
  const currentUserAreaId = user?.saleAreaId || undefined;

  const [filterOpen, setFilterOpen] = useState(true);
  const [monthValue, setMonthValue] = useState(() => moment().format("MM-YYYY"));
  const [status, setStatus] = useState(ALL_VALUE);
  const [area, setArea] = useState(ALL_OPTION);
  const [block, setBlock] = useState(ALL_OPTION);
  const [branch, setBranch] = useState(ALL_OPTION);
  const [sortKey, setSortKey] = useState("NEWEST");

  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [loading, setLoading] = useState(false);
  const reqId = useRef(0);

  const sheet = useSheet();
  const [sheetSearch, setSheetSearch] = useState("");
  const sheetExtra = useMemo(
    () => (sheet.type === "BLOCK" ? { saleAreaId: validId(area) } : undefined),
    [sheet.type, area]
  );
  const options = useFilterOptions(sheet.type, sheetSearch, sheetExtra);

  const buildParams = useCallback(
    (p) => {
      const [month, year] = monthValue.split("-");
      return {
        page: p,
        limit: LIMIT,
        month: Number(month),
        year: Number(year),
        status: status !== ALL_VALUE ? status : undefined,
        areaId: isAreaLockedToUser ? currentUserAreaId : validId(area),
        blockId: validId(block),
        branchId: validId(branch),
      };
    },
    [monthValue, status, area, block, branch, isAreaLockedToUser, currentUserAreaId]
  );

  const fetchPage = useCallback(
    async (p, mode) => {
      reqId.current += 1;
      const rid = reqId.current;
      setLoading(true);
      try {
        const data = await getBlockKpiListApi(buildParams(p));
        if (rid !== reqId.current) return;
        const list = data?.items || [];
        setItems((prev) => (mode === "append" ? [...prev, ...list] : list));
        setHasNext(!!data?.pagination?.hasNextPage);
        if (data?.summary) setSummary(data.summary);
        setPage(p);
      } catch {
        if (rid === reqId.current && mode !== "append") setItems([]);
      } finally {
        if (rid === reqId.current) setLoading(false);
      }
    },
    [buildParams]
  );

  useEffect(() => {
    fetchPage(1, "replace");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthValue, status, area?._id, block?._id, branch?._id, sortKey]);

  const monthLabel = monthOptions.find((m) => m.value === monthValue)?.label || monthValue;
  const statusLabel = STATUS_OPTIONS.find((s) => s.key === status)?.label ?? "Tất cả";
  const sortLabel = SORT_OPTIONS.find((s) => s.key === sortKey)?.label ?? "Mới đến cũ";
  const cls = summary?.classifications || {};

  // Lưới bộ lọc theo chức vụ - khớp renderFilterGrid của app (Vùng chỉ dành cho GĐ/TGD).
  const filterFields = useMemo(() => {
    const f = {
      status: <FilterField key="status" label="Trạng thái" value={statusLabel} onClick={() => sheet.open("STATUS")} />,
      branch: (
        <FilterField key="branch" label="Chi nhánh" value={branch?.name || "Tất cả"} onClick={() => sheet.open("BRANCH")} />
      ),
      area: <FilterField key="area" label="Vùng" value={area?.name || "Tất cả"} onClick={() => sheet.open("AREA")} />,
      block: <FilterField key="block" label="Khối" value={block?.name || "Tất cả"} onClick={() => sheet.open("BLOCK")} />,
      month: <FilterField key="month" label="Tháng" value={monthLabel} onClick={() => sheet.open("MONTH")} />,
    };
    const pos = supportDept ? JOB_TITLE.TGD : jobPosition;
    switch (pos) {
      case JOB_TITLE.NHAN_VIEN:
        return [f.status, f.month];
      case JOB_TITLE.TRUONG_KHOI:
      case JOB_TITLE.TRUONG_VUNG:
        return [f.status, f.branch, f.block, f.month];
      case JOB_TITLE.TRUONG_PHONG:
        return [f.status, f.branch, f.month];
      case JOB_TITLE.TGD:
      case JOB_TITLE.PTGD:
      case JOB_TITLE.GDCN:
        return [f.status, f.branch, f.area, f.block, f.month];
      default:
        return [f.status, f.branch, f.month];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobPosition, supportDept, statusLabel, branch, area, block, monthLabel]);

  const sheetTitle =
    { STATUS: "Trạng thái", MONTH: "Chọn tháng", AREA: "Vùng", BLOCK: "Khối", BRANCH: "Chi nhánh", SORT: "Sắp xếp" }[
      sheet.type
    ] || "";

  const sheetData = useMemo(() => {
    if (sheet.type === "STATUS") return STATUS_OPTIONS.map((x) => ({ key: x.key, label: x.label, raw: x.key }));
    if (sheet.type === "SORT") return SORT_OPTIONS.map((x) => ({ key: x.key, label: x.label, raw: x.key }));
    if (sheet.type === "MONTH") return monthOptions.map((x) => ({ key: x.value, label: x.label, raw: x.value }));
    if (sheet.type === "AREA") return buildOptionSheetData(options.items, area);
    if (sheet.type === "BLOCK") return buildOptionSheetData(options.items, block);
    if (sheet.type === "BRANCH") return buildOptionSheetData(options.items, branch);
    return [];
  }, [sheet.type, options.items, area, block, branch, monthOptions]);

  const selectedKey = {
    STATUS: status,
    SORT: sortKey,
    MONTH: monthValue,
    AREA: area?._id,
    BLOCK: block?._id,
    BRANCH: branch?._id,
  }[sheet.type];

  const onPick = (raw) => {
    if (sheet.type === "STATUS") setStatus(raw);
    if (sheet.type === "SORT") setSortKey(raw);
    if (sheet.type === "MONTH") setMonthValue(raw);
    if (sheet.type === "AREA") setArea(raw);
    if (sheet.type === "BLOCK") setBlock(raw);
    if (sheet.type === "BRANCH") setBranch(raw);
    sheet.close();
    setSheetSearch("");
  };

  const selectSheet = (
    <SelectSheet
      isOpen={sheet.isOpen}
      onClose={() => {
        sheet.close();
        setSheetSearch("");
      }}
      title={sheetTitle}
      data={sheetData}
      selectedKey={selectedKey}
      onSelect={onPick}
      searchable={["AREA", "BLOCK", "BRANCH"].includes(sheet.type)}
      searchValue={sheetSearch}
      onChangeSearch={setSheetSearch}
      loading={options.loading}
      loadingMore={options.loadingMore}
      hasNextPage={options.hasMore}
      onEndReached={options.loadMore}
    />
  );

  // ===================== Bản WEB (desktop) =====================
  if (isDesktop) {
    const rows = items.map((it, i) => {
      const clsItem = it?.classification || {};
      const ui = blockKpiStatusUi(clsItem?.code);
      const m = it?.metrics || {};
      return {
        id: it?.block?.code || i,
        code: it?.block?.code || "",
        ui,
        block: it?.block?.name || "-",
        manager: `${it?.block?.managerName || "-"}${
          it?.block?.managerCode ? ` (${it.block.managerCode})` : ""
        }`,
        area: it?.block?.areaName || "-",
        branch: it?.block?.branchName || "-",
        classLabel: clsItem?.label || BLOCK_KPI_STATUS_LABELS[clsItem?.code] || "-",
        recruitment: fmtScore(m.recruitmentScore || 0),
        attendance: fmtScore(m.attendanceScore || 0),
        training: fmtScore(m.trainingScore || 0),
        transaction: fmtScore(m.transactionScore || 0),
        staff: `${Number(m.newStaffCount || 0)} / ${Number(m.resignedStaffCount || 0)}`,
        score: fmtScore(m.scorePercent ?? m.score ?? 0),
      };
    });

    const openDetail = (row) => {
      const [month, year] = monthValue.split("-");
      navigate(
        `${EMP_BASE_PATH}/quan-ly/bao-cao/kpi-khoi/${encodeURIComponent(
          row.code,
        )}?month=${Number(month)}&year=${Number(year)}`,
      );
    };

    return (
      <DesktopPage title="KPI khối">
        <DesktopStatRow
          items={[
            { key: "total", title: "Tổng số khối", total: summary?.totalBlocks ?? 0 },
            { key: "xs", title: "Khối xuất sắc", total: cls.XUAT_SAC ?? 0, color: "#2F80ED" },
            { key: "tot", title: "Khối đạt tốt", total: cls.TOT ?? 0, color: "#2ABA82" },
            { key: "ccg", title: "Cần cố gắng thêm", total: cls.CAN_CO_GANG ?? 0, color: "#8B5CF6" },
            { key: "cb", title: "Cần lưu ý", total: cls.CANH_BAO ?? 0, color: "#F57127" },
          ]}
        />

        <DesktopPanel p="0">
          <Flex p="16px" gap="16px" flexWrap="wrap" align="flex-end">
            {filterFields.map((el, i) =>
              React.cloneElement(el, { key: el.key || i, width: "200px" }),
            )}
            <Box w="200px">
              <Text fontSize="13px" color={M.subTitle} mb="4px">
                Sắp xếp
              </Text>
              <Box
                as="button"
                w="100%"
                h="40px"
                px="12px"
                textAlign="left"
                border="1px solid"
                borderColor={M.border}
                borderRadius="8px"
                bg="white"
                fontSize="13px"
                onClick={() => sheet.open("SORT")}
              >
                {sortLabel}
              </Box>
            </Box>
          </Flex>

          <DesktopTable
            loading={loading && !items.length}
            onRowClick={openDetail}
            rows={rows}
            headers={[
              { key: "block", label: "Khối", align: "left", width: "15%" },
              { key: "manager", label: "GĐ khối", align: "left", width: "15%" },
              { key: "area", label: "Vùng", align: "left", width: "10%" },
              { key: "branch", label: "Chi nhánh", align: "left", width: "10%" },
              {
                key: "classLabel",
                label: "Xếp loại",
                width: "10%",
                render: (row) => (
                  <Box display="inline-block" bg={row.ui.bg} borderRadius="6px" px="8px" py="3px">
                    <Text fontSize="13px" fontWeight={600} color={row.ui.color}>
                      {row.classLabel}
                    </Text>
                  </Box>
                ),
              },
              { key: "recruitment", label: "Tuyển dụng", width: "7%" },
              { key: "attendance", label: "Chuyên cần", width: "7%" },
              { key: "training", label: "Đào tạo", width: "7%" },
              { key: "transaction", label: "Giao dịch", width: "7%" },
              { key: "staff", label: "Tuyển mới / Nghỉ", width: "7%" },
              {
                key: "score",
                label: "Điểm số",
                width: "5%",
                render: (row) => (
                  <Text fontSize="14px" fontWeight={600} color={M.primary}>
                    {row.score}
                  </Text>
                ),
              },
            ]}
          />

          <Box px="16px" pb="16px">
            <LoadMore
              hasNext={hasNext}
              loading={loading}
              onClick={() => fetchPage(page + 1, "append")}
            />
          </Box>
        </DesktopPanel>

        {selectSheet}
      </DesktopPage>
    );
  }

  return (
    <Box maxW="480px" mx="auto" pb="40px">
      <AppBar title="KPI khối" />

      <Box px="16px" pt="12px">
        <FilterPanel open={filterOpen} onToggle={() => setFilterOpen((p) => !p)}>
          {filterFields}
        </FilterPanel>

        <Box h="12px" />
        <SummaryCard text="TỔNG SỐ KHỐI" total={summary?.totalBlocks ?? 0} />

        <Box h="12px" />
        <Flex flexWrap="wrap" justify="space-between" gap="12px">
          <StatCard
            subTitle="XUẤT SẮC"
            title="Khối xuất sắc"
            value={cls.XUAT_SAC ?? 0}
            icon={appImages.icon_award}
            bgColor="#EFF6FF"
            textColor="#2F80ED"
          />
          <StatCard
            subTitle="TỐT"
            title="Khối đạt tốt"
            value={cls.TOT ?? 0}
            icon={appImages.icon_trending_up}
            bgColor="#EDFDF5"
            textColor="#2ABA82"
          />
          <StatCard
            subTitle="CẦN CỐ GẮNG"
            title="Cần cố gắng thêm"
            value={cls.CAN_CO_GANG ?? 0}
            icon={appImages.icon_heart_handshake}
            bgColor="#F5F3FF"
            textColor="#8B5CF6"
          />
          <StatCard
            subTitle="CẢNH BÁO"
            title="Cần lưu ý"
            value={cls.CANH_BAO ?? 0}
            icon={appImages.icon_triangle_alert}
            bgColor="#FFF7ED"
            textColor="#F57127"
          />
        </Flex>

        <ListHeaderRow title="Danh sách khối" sortLabel={sortLabel} onSort={() => sheet.open("SORT")} mb="12px" />

        {loading && !items.length ? (
          <CenterSpinner />
        ) : items.length === 0 ? (
          <EmptyText />
        ) : (
          <Flex direction="column" gap="12px">
            {items.map((it, i) => (
              <BlockKpiCard
                key={it?.block?.code || i}
                item={it}
                onClick={() => {
                  const [month, year] = monthValue.split("-");
                  navigate(
                    `${EMP_BASE_PATH}/quan-ly/bao-cao/kpi-khoi/${encodeURIComponent(
                      it?.block?.code || ""
                    )}?month=${Number(month)}&year=${Number(year)}`
                  );
                }}
              />
            ))}
            <LoadMore hasNext={hasNext} loading={loading} onClick={() => fetchPage(page + 1, "append")} />
          </Flex>
        )}
      </Box>

      {selectSheet}
    </Box>
  );
};

export default BlockKpiList;
